
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Award, 
  MoreHorizontal, 
  Eye, 
  Info,
  FileDown,
  Calendar,
  Clock,
  BookOpen,
  Download,
  FileCheck,
  Send
} from "lucide-react";
import { generateCertificate, generateCertificateHtml } from "@/lib/certificates/generate-certificate";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cn, formatCEU } from "@/lib/utils";
import { useState, useEffect } from "react";
import { FeedbackDialog } from "./FeedbackDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface CertificateCardProps {
  certificate: {
    id: string;
    course_title: string;
    completion_date: string;
    hours: number;
    category?: string;
    subject_area?: string;
    feedback_submitted?: boolean;
  };
  onView: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const getCategoryColor = (category?: string): { color: string, bgColor: string } => {
  switch (category?.toLowerCase()) {
    case 'professional development':
      return { color: 'text-primary', bgColor: 'bg-accent-subtle' };
    case 'technology':
      return { color: 'text-accent', bgColor: 'bg-accent-subtle/80' };
    case 'leadership':
      return { color: 'text-primary', bgColor: 'bg-secondary/20' };
    case 'curriculum':
      return { color: 'text-accent', bgColor: 'bg-accent-light/20' };
    case 'special education':
      return { color: 'text-primary', bgColor: 'bg-secondary/30' };
    case 'cultural competency':
      return { color: 'text-accent', bgColor: 'bg-accent-subtle/90' };
    default:
      return { color: 'text-primary', bgColor: 'bg-accent-light/20' };
  }
};

export const CertificateCard = ({ certificate, onView, className, style }: CertificateCardProps) => {
  const { toast } = useToast();
  const categoryColors = getCategoryColor(certificate.category);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(certificate.feedback_submitted || false);
  const [certificateDetails, setCertificateDetails] = useState<{id: string, feedback_submitted: boolean, course_id: string} | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [bosaRequestDialogOpen, setBosaRequestDialogOpen] = useState(false);
  const [bosaRequestNotes, setBosaRequestNotes] = useState("");
  const [bosaRequestLoading, setBosaRequestLoading] = useState(false);
  const [bosaRequestStatus, setBosaRequestStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
  const [bosaDocUrl, setBosaDocUrl] = useState<string | null>(null);

  console.log(`[CERT-DEBUG] Component mount/update for certificate ${certificate.id}`);
  console.log(`[CERT-DEBUG] Initial feedbackSubmitted value from prop:`, certificate.feedback_submitted);

  useEffect(() => {
    const fetchCertificateDetails = async () => {
      setIsLoading(true);
      console.log(`[CERT-DEBUG] Fetching certificate details for ID: ${certificate.id}, refreshKey: ${dataRefreshKey}`);
      
      try {
        const { data: certData, error: certError } = await supabase
          .from('certificates')
          .select('id, feedback_submitted, course_id')
          .eq('id', certificate.id)
          .single();

        if (certError) {
          console.error('[CERT-ERROR] Error fetching certificate:', certError);
          throw certError;
        }

        if (!certData) {
          console.error('[CERT-ERROR] No certificate data found for ID:', certificate.id);
          setIsLoading(false);
          return;
        }

        console.log('[CERT-DEBUG] Certificate data loaded:', certData);
        console.log('[CERT-DEBUG] feedback_submitted value:', certData.feedback_submitted);
        
        setCertificateDetails(certData);
        setFeedbackSubmitted(!!certData.feedback_submitted);
        
        // Check for BOSA certificate request
        const { data: bosaData, error: bosaError } = await supabase
          .from('bosa_certificate_requests')
          .select('status, bosa_certificate_url')
          .eq('certificate_id', certificate.id)
          .maybeSingle();
        
        if (!bosaError && bosaData) {
          console.log('[CERT-DEBUG] BOSA request found:', bosaData);
          setBosaRequestStatus(bosaData.status as any);
          setBosaDocUrl(bosaData.bosa_certificate_url);
        } else {
          setBosaRequestStatus('none');
          setBosaDocUrl(null);
        }
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          console.error('[CERT-ERROR] No user ID available for registration lookup');
          return;
        }
        
        console.log('[CERT-DEBUG] Looking up registration for user:', user.id, 'and course:', certData.course_id);
        
        const { data: regData, error: regError } = await supabase
          .from('course_registrations')
          .select('id')
          .eq('course_id', certData.course_id)
          .eq('user_id', user.id)
          .single();

        if (regError) {
          console.error('[CERT-ERROR] Error fetching registration:', regError);
          throw regError;
        }

        if (regData) {
          console.log('[CERT-DEBUG] Registration data loaded:', regData);
          setRegistrationId(regData.id);
        } else {
          console.warn('[CERT-WARN] No registration found for this certificate/user');
        }
      } catch (error) {
        console.error('[CERT-ERROR] Error in fetchCertificateDetails:', error);
        toast({
          title: "Error",
          description: "Failed to load certificate details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCertificateDetails();
  }, [certificate.id, toast, dataRefreshKey]);

  useEffect(() => {
    console.log(`[CERT-DEBUG] Certificate prop changed, updating feedbackSubmitted to:`, certificate.feedback_submitted);
    setFeedbackSubmitted(!!certificate.feedback_submitted);
  }, [certificate.feedback_submitted]);

  const getFileName = (format?: string) => {
    const date = new Date(certificate.completion_date).toISOString().split('T')[0];
    const safeTitleName = certificate.course_title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const baseName = `NWSISD-CEU-${safeTitleName}-${date}`;
    return format ? `${baseName}.${format}` : `${baseName}.pdf`;
  };

  const handleDownload = async () => {
    try {
      console.log('[CERT-DEBUG] handleDownload called');
      console.log('[CERT-DEBUG] feedbackSubmitted state:', feedbackSubmitted);
      console.log('[CERT-DEBUG] certificateDetails:', certificateDetails);
      console.log('[CERT-DEBUG] registrationId:', registrationId);
      
      if (certificateDetails?.id) {
        const { data: refreshedCertData } = await supabase
          .from('certificates')
          .select('feedback_submitted')
          .eq('id', certificateDetails.id)
          .single();
          
        if (refreshedCertData) {
          console.log('[CERT-DEBUG] Refreshed feedback status:', refreshedCertData.feedback_submitted);
          setFeedbackSubmitted(!!refreshedCertData.feedback_submitted);
          
          if (!feedbackSubmitted && !refreshedCertData.feedback_submitted && certificateDetails && registrationId) {
            console.log('[CERT-DEBUG] Opening feedback dialog - feedback not submitted yet');
            setFeedbackDialogOpen(true);
            return;
          }
        }
      }
      
      console.log('[CERT-DEBUG] Proceeding to download PDF - feedback already submitted or status check failed');
      handleDownloadPDF();
    } catch (error) {
      console.error('[CERT-ERROR] Error in handleDownload:', error);
      toast({
        title: "Error",
        description: "Failed to handle download. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFeedbackSubmitted = async () => {
    try {
      console.log('[CERT-DEBUG] Feedback submitted callback triggered');
      
      if (!certificateDetails?.id) {
        console.error('[CERT-ERROR] No certificate ID available');
        return;
      }

      setFeedbackSubmitted(true);
      
      console.log('[CERT-DEBUG] Forcing refresh of certificate data');
      setDataRefreshKey(prevKey => prevKey + 1);
      
      const { data: verifyData } = await supabase
        .from('certificates')
        .select('feedback_submitted')
        .eq('id', certificateDetails.id)
        .single();
        
      console.log('[CERT-DEBUG] Verification check after feedback submission:', verifyData);
      
      if (verifyData && !verifyData.feedback_submitted) {
        console.warn('[CERT-WARN] Database still shows feedback_submitted=false, retrying update');
        
        await supabase
          .from("certificates")
          .update({ feedback_submitted: true })
          .eq("id", certificateDetails.id);
      }
      
      setTimeout(() => {
        console.log('[CERT-DEBUG] Triggering PDF download after feedback submission');
        handleDownloadPDF();
      }, 1000);
    } catch (error) {
      console.error('[CERT-ERROR] Error in handleFeedbackSubmitted:', error);
      toast({
        title: "Error",
        description: "Failed to process feedback submission. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewDetails = () => {
    toast({
      title: "Certificate Details",
      description: `${certificate.course_title} - Completed on ${new Date(certificate.completion_date).toLocaleDateString()} (${formatCEU(certificate.hours)} CEUs)`,
    });
  };

  const handleDownloadPDF = async () => {
    try {
      console.log('[CERT-DEBUG] Starting PDF generation');
      setPdfGenerating(true);
      
      toast({
        title: "Generating Certificate",
        description: "Your certificate is being prepared...",
      });
      
      const certificateData = await generateCertificate(supabase, certificate.id);
      
      if (!certificateData) {
        throw new Error("Failed to generate certificate data");
      }

      console.log('[CERT-DEBUG] Certificate data generated successfully');

      const html = generateCertificateHtml(certificateData);
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        throw new Error("Could not open print window. Please check if pop-ups are blocked.");
      }
      
      printWindow.document.write(html);
      printWindow.document.write(`
        <script>
          document.title = "${getFileName()}";
          setTimeout(() => {
            window.print();
            setTimeout(() => window.close(), 500);
          }, 1000);
        </script>
      `);
      printWindow.document.close();
      
      toast({
        title: "Certificate Ready",
        description: "Please use your browser's print dialog to save as PDF.",
      });
    } catch (error) {
      console.error('[CERT-ERROR] Error downloading PDF certificate:', error);
      toast({
        title: "Error",
        description: "Failed to generate certificate. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const openBosaRequestDialog = () => {
    if (!feedbackSubmitted) {
      toast({
        title: "Feedback Required",
        description: "Please provide feedback before requesting a BOSA certificate.",
        variant: "destructive",
      });
      return;
    }
    setBosaRequestDialogOpen(true);
  };

  const handleBosaRequest = async () => {
    try {
      if (!certificateDetails?.id) {
        throw new Error("Certificate details not available");
      }
      
      setBosaRequestLoading(true);
      
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      
      // Check if a request already exists
      const { data: existingRequest } = await supabase
        .from('bosa_certificate_requests')
        .select('id')
        .eq('certificate_id', certificateDetails.id)
        .maybeSingle();
      
      if (existingRequest) {
        toast({
          title: "Request Exists",
          description: "You have already submitted a BOSA certificate request for this course.",
        });
        setBosaRequestDialogOpen(false);
        return;
      }
      
      const { error } = await supabase.from('bosa_certificate_requests').insert({
        certificate_id: certificateDetails.id,
        user_id: user.id,
        user_notes: bosaRequestNotes,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Request Submitted",
        description: "Your BOSA CEU certificate request has been submitted successfully.",
      });
      
      // Refresh the data to update the status
      setDataRefreshKey(prev => prev + 1);
      setBosaRequestStatus('pending');
      setBosaRequestDialogOpen(false);
    } catch (error) {
      console.error('[BOSA-ERROR] Error submitting BOSA request:', error);
      toast({
        title: "Error",
        description: "Failed to submit BOSA certificate request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBosaRequestLoading(false);
    }
  };

  const handleBosaDownload = () => {
    if (bosaDocUrl) {
      window.open(bosaDocUrl, '_blank');
    } else {
      toast({
        title: "Not Available",
        description: "BOSA certificate is not available for download yet.",
        variant: "destructive",
      });
    }
  };

  const getBosaStatusDisplay = () => {
    switch (bosaRequestStatus) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            BOSA Request Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            BOSA Request Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            BOSA Request Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card 
        className={cn(
          "hover:shadow-md transition-all duration-300 hover:-translate-y-1 bg-white rounded-lg",
          className
        )} 
        style={style}
      >
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <h3 className="font-title text-xl text-primary font-bold leading-tight">
                {certificate.course_title}
              </h3>
              <div className="flex flex-wrap gap-2">
                <Calendar className="h-5 w-5 text-primary/70" />
                <span className="text-sm font-body text-gray-600">
                  {new Date(certificate.completion_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary/70" />
                  <span className="text-sm font-body text-gray-600">{formatCEU(certificate.hours)} CEUs</span>
                </div>
                
                {certificate.category && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary/70" />
                    <span className="text-sm font-body text-gray-600">{certificate.category}</span>
                  </div>
                )}
              </div>
              
              {bosaRequestStatus !== 'none' && (
                <div className="mt-2">
                  {getBosaStatusDisplay()}
                </div>
              )}
            </div>
            
            <div className="ml-4">
              <div className="rounded-full bg-accent-subtle p-3">
                <Award className="h-6 w-6 text-primary" />
              </div>
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="p-4 pt-0 gap-2 flex-wrap">
          <Button 
            size="sm" 
            variant="outline"
            className={cn(
              "flex-1 font-body justify-center border border-primary/20 hover:bg-primary hover:text-white",
              "transition-all duration-300"
            )}
            onClick={!feedbackSubmitted ? handleDownload : handleDownloadPDF}
            disabled={isLoading || pdfGenerating}
          >
            {(isLoading || pdfGenerating) ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2"></span>
            ) : (
              <Download className="h-5 w-5 mr-2" />
            )}
            {!feedbackSubmitted 
              ? "Provide Feedback" 
              : "Download Certificate"
            }
          </Button>
          
          {feedbackSubmitted && bosaRequestStatus === 'none' && (
            <Button 
              size="sm"
              variant="outline"
              className="font-body border border-primary/20 hover:bg-accent hover:text-white transition-all duration-300"
              onClick={openBosaRequestDialog}
            >
              <Send className="h-5 w-5 mr-2" />
              Request BOSA CEU
            </Button>
          )}
          
          {bosaRequestStatus === 'approved' && bosaDocUrl && (
            <Button 
              size="sm"
              variant="outline"
              className="font-body border border-primary/20 hover:bg-accent hover:text-white transition-all duration-300"
              onClick={handleBosaDownload}
            >
              <FileCheck className="h-5 w-5 mr-2" />
              BOSA Certificate
            </Button>
          )}
        </CardFooter>
      </Card>

      {certificateDetails && registrationId && (
        <FeedbackDialog
          open={feedbackDialogOpen}
          onOpenChange={setFeedbackDialogOpen}
          certificateId={certificateDetails.id}
          registrationId={registrationId}
          onFeedbackSubmitted={handleFeedbackSubmitted}
        />
      )}
      
      {/* BOSA Request Dialog */}
      <Dialog open={bosaRequestDialogOpen} onOpenChange={setBosaRequestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-primary">Request BOSA CEU Certificate</DialogTitle>
            <DialogDescription className="font-body">
              Submit a request for an official Board of School Administrators (BOSA) CEU certificate.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold font-section text-primary">Course Information</h4>
              <p className="text-sm font-body">{certificate.course_title}</p>
              <p className="text-sm font-body text-muted-foreground">
                {formatCEU(certificate.hours)} CEUs • Completed on {new Date(certificate.completion_date).toLocaleDateString()}
              </p>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="notes" className="text-sm font-semibold font-section text-primary">
                Additional Notes (Optional)
              </label>
              <Textarea
                id="notes"
                placeholder="Provide any additional information about your request..."
                value={bosaRequestNotes}
                onChange={(e) => setBosaRequestNotes(e.target.value)}
                className="font-body"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              variant="default"
              onClick={handleBosaRequest}
              disabled={bosaRequestLoading}
              className="font-body"
            >
              {bosaRequestLoading ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></span>
                  Processing
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Request
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBosaRequestDialogOpen(false)}
              className="font-body"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
